import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useWallet } from "@/context/WalletContext";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import OwnerView from "@/components/atomics/owner-view";
import EditView from "@/components/atomics/edit-view";
import LoadingStatus from "@/components/atomics/loading-status";
import OwnerHeader from "@/components/molecules/owner/owner-header";
import OwnerStatsComponent from "@/components/molecules/owner/owner-stats";
import OwnerSections from "@/components/molecules/owner/owner-section";
import OwnerModals from "@/components/molecules/owner/owner-modal";
import { OwnerEditViewProps } from "@/types/owner-edit";
import { useOwnerData } from "@/hooks/useOwnerData";
import { useOwnerEditView } from "@/hooks/useOwnerEditView";
import { useOwnerValidation } from "@/hooks/useOwnerValidation";
import { useOwnerSave } from "@/hooks/useOwnerSave";
import { ownerEditUtils } from "@/utils/owner-edit";
import { OWNER_EDIT_CONSTANTS } from "@/constants/owner-edit";
import { backend } from "@/services/backend";
import { ratingBackend } from "@/services/ratingBackend";
import OwnerCollection from "./owner-collection";
import { useOwner } from "@/context/OwnerContext";
import { useStarPayment } from "@/hooks/useStarPayment";

const OwnerEditView: React.FC<OwnerEditViewProps> = ({
  className,
  ownerDatas,
}) => {
  const { address } = useParams<{ address: string }>();
  const { walletAddress } = useWallet();
  const { validateBeforeSave } = useOwnerValidation();
  const { isEditView } = useOwner();
  const { isSaving, saveOwnerData } = useOwnerSave();
  const [countProfile, setReloadProfile] = useState(0);
  const [ownerStars, setOwnerStars] = useState<number>(0);

  const {
    ownerData,
    setOwnerData,
    originalOwnerData,
    setOriginalOwnerData,
    isLoading,
    error,
    stats,
    hasChanges,
  } = useOwnerData(address || undefined, ownerDatas, countProfile);

  const { sendStars } = useStarPayment({
    onSuccess: async () => {
      if (address) {
        const updatedRatingData =
          await ratingBackend.getOwnerRatingData(address);
        if (updatedRatingData?.stars !== undefined) {
          setOwnerStars(updatedRatingData.stars);
        }
      }
    },
  });

  const {
    viewState,
    modalState,
    files,
    toggleView,
    showEdit,
    exitEdit,
    openModal,
    closeModal,
    updateFile,
    resetFiles,
  } = useOwnerEditView(walletAddress, address || undefined);

  const handleUpdateOwnerData = (data: Partial<typeof ownerData>) => {
    setOwnerData(prev => ({ ...prev, ...data }));
  };

  const reloadProfile = () => {
    setTimeout(() => {
      setReloadProfile(state => state + 1);
    }, 101);
  };

  useEffect(() => {
    const fetchOwnerStars = async () => {
      if (!address) return;
      const ratingData = await ratingBackend.getOwnerRatingData(address);
      if (ratingData?.stars !== undefined) {
        setOwnerStars(ratingData.stars);
      }
    };
    fetchOwnerStars();
  }, [address]);

  const handleStarVote = async (starCount: number) => {
    if (!address) return;
    await sendStars(address, starCount);
  };

  const handleUpdateAvatar = (
    file: File | string,
    nftNumber: string | null,
    index?: number
  ) => {
    if (file) {
      // If it's a string (NFT URL), don't store it as a file, but store the NFT info
      if (typeof file === "string") {
        updateFile("avatar", null, nftNumber, index);
        setOwnerData(prev => ({
          ...prev,
          avatar: file, // Use the string URL directly
          extendInfo: prev.extendInfo
            ? {
                ...prev.extendInfo,
                avatar_index_in: index ?? null,
              }
            : null,
        }));
      } else {
        // It's a File object
        updateFile("avatar", file, nftNumber, index);
        setOwnerData(prev => ({
          ...prev,
          avatar: ownerEditUtils.createFilePreviewUrl(file),
          extendInfo: prev.extendInfo
            ? {
                ...prev.extendInfo,
                avatar_index_in: index ?? null,
              }
            : null,
        }));
      }
      // reloadProfile();
    }
  };

  const handleUpdateBackground = (
    file: File | string,
    nftNumber: string | null,
    index?: number
  ) => {
    if (file) {
      // If it's a string (NFT URL), don't store it as a file, but store the NFT info
      if (typeof file === "string") {
        updateFile("background", null, nftNumber, index);
        setOwnerData(prev => ({
          ...prev,
          background: file, // Use the string URL directly
          backgroundIndex: index,
          extendInfo: prev.extendInfo
            ? {
                ...prev.extendInfo,
                background_index_in: index ?? null,
              }
            : null,
        }));
      } else {
        // It's a File object
        updateFile("background", file, nftNumber, index);
        setOwnerData(prev => ({
          ...prev,
          background: ownerEditUtils.createFilePreviewUrl(file),
          backgroundIndex: index,
          extendInfo: prev.extendInfo
            ? {
                ...prev.extendInfo,
                background_index_in: index ?? null,
              }
            : null,
        }));
      }
      // reloadProfile();
    }
  };

  const handleRemoveAvatar = () => {
    const nftAvatar = ownerData.extendInfo?.avatar_address;
    if (nftAvatar) {
      backend.deSelectUserSkin("avatar");
    } else {
      updateFile("avatar", null, null);
    }
    setOwnerData(prev => ({
      ...prev,
      avatar: OWNER_EDIT_CONSTANTS.DEFAULT_AVATAR,
      avatarId: -1,
      extendInfo: prev.extendInfo
        ? {
            ...prev.extendInfo,
            avatar_index_in: null,
          }
        : null,
    }));
  };

  const handleUpdateInfo = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOwnerData(prev => ({
      ...prev,
      info: e.target.value,
    }));
  };

  const handleUpdateStats = (index: number) => {
    setOwnerData(prev => ({
      ...prev,
      stats: ownerEditUtils.updateStatVisibility(prev.stats, index),
    }));
    // reloadProfile();
  };

  const handleUpdateSocial = (index: number, value: string) => {
    setOwnerData(prev => ({
      ...prev,
      socials: ownerEditUtils.updateSocialLink(prev.socials, index, value),
    }));
  };

  const handleSave = async () => {
    if (!address) {
      enqueueSnackbar("Invalid address", {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "error",
      });
      return;
    }

    if (!validateBeforeSave(ownerData)) {
      return;
    }

    try {
      const result = await saveOwnerData(
        ownerData,
        originalOwnerData,
        files,
        address!
      );

      console.log("Save result:", result);

      if (result.success) {
        console.log("Save successful, exiting edit mode");
        exitEdit();
        setOriginalOwnerData(ownerData);
        resetFiles();
      } else {
        console.log("Save failed with errors:", result.errors);
        setOwnerData(originalOwnerData);
        result.errors.forEach(error => {
          enqueueSnackbar(error, {
            SnackbarProps: {
              onClick: () => {
                closeSnackbar();
              },
            },
            variant: "error",
          });
        });
      }
    } catch (error) {
      console.error("Error saving owner data:", error);
      enqueueSnackbar("Unexpected error occurred", {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "error",
      });
      setOwnerData(originalOwnerData);
    } finally {
      reloadProfile();
    }
  };

  const handleCancel = () => {
    setOwnerData(originalOwnerData);
    resetFiles();
    exitEdit();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen">
        <LoadingStatus status="loading" text={["Loading..."]} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-screen justify-center items-center">
        <div className="text-primary-red text-center">
          <p>Error loading owner data</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!address) {
    return (
      <div className="flex flex-col h-screen justify-center items-center">
        <div className="text-primary-red text-center">
          <p>Invalid address</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <OwnerModals
        viewState={viewState}
        modalState={modalState}
        ownerData={ownerData}
        address={address!}
        onUpdateAvatar={handleUpdateAvatar}
        onUpdateBackground={handleUpdateBackground}
        onCloseAvatarModal={() => closeModal("showAvatarModal")}
        onCloseBackgroundModal={() => closeModal("showBackgroundModal")}
        onCloseReportModal={() => closeModal("showReportModal")}
      />

      <div className={`w-full relative ${className || ""}`}>
        {!viewState.isEditView && viewState.showOwnerView && (
          <OwnerView
            isOwnerView={viewState.isOwnerView}
            handleViewToggle={toggleView}
          />
        )}

        {viewState.isEditView && viewState.showOwnerView && (
          <EditView
            className="fixed top-[90px] left-[50%] translate-x-[-50%] z-[1] bg-black"
            handleSave={handleSave}
            handleCancel={handleCancel}
            isChange={hasChanges}
            isSaving={isSaving}
          />
        )}

        <OwnerHeader
          ownerData={ownerData}
          viewState={viewState}
          address={address!}
          walletAddress={walletAddress}
          stars={ownerStars}
          onStarVote={handleStarVote}
          onUpdateOwnerData={handleUpdateOwnerData}
          onUpdateBackground={handleUpdateBackground}
          onUpdateAvatar={handleUpdateAvatar}
          onRemoveAvatar={handleRemoveAvatar}
          onShowEdit={showEdit}
          onShowBackgroundModal={() => openModal("showBackgroundModal")}
          onShowAvatarModal={() => openModal("showAvatarModal")}
          onShowReportModal={() => openModal("showReportModal")}
        />

        <div className="px-[30px] pt-[193px]">
          <OwnerStatsComponent
            ownerData={ownerData}
            viewState={viewState}
            stats={stats}
            onUpdateStats={handleUpdateStats}
          />

          {/* ownerDatas?.collections && (
            <OwnerCollection collectionData={ownerDatas.collections} />
          ) */}

          <OwnerSections
            ownerData={ownerData}
            viewState={viewState}
            onUpdateInfo={handleUpdateInfo}
            onUpdateSocial={handleUpdateSocial}
          />
        </div>
      </div>
    </>
  );
};

export default OwnerEditView;
